import type { EventHub } from '../events/EventHub';
import { t } from '../i18n';

export interface PauseChangeDetail {
  paused: boolean;
}

export type PauseChangeEvent = CustomEvent<PauseChangeDetail>;

export const PAUSE_CHANGE_EVENT = 'pause:changed';

interface PauseControllerOptions {
  overlay: HTMLElement;
  toggleButton: HTMLButtonElement;
  statusElement?: HTMLElement | null;
  eventHub?: EventHub;
}

export class PauseController extends EventTarget {
  private readonly overlay: HTMLElement;
  private readonly toggleButton: HTMLButtonElement;
  private readonly statusElement: HTMLElement | null;
  private readonly eventHub?: EventHub;
  private paused = false;

  constructor(options: PauseControllerOptions) {
    super();
    this.overlay = options.overlay;
    this.toggleButton = options.toggleButton;
    this.statusElement = options.statusElement ?? null;
    this.eventHub = options.eventHub;

    this.toggleButton.addEventListener('click', this.handleToggleClick);
  }

  public dispose(): void {
    this.toggleButton.removeEventListener('click', this.handleToggleClick);
  }

  public get isPaused(): boolean {
    return this.paused;
  }

  public setPaused(next: boolean): void {
    if (this.paused === next) {
      return;
    }

    this.paused = next;
    this.overlay.hidden = !this.paused;
    this.setStatus(this.paused ? t('pause.statusPaused') : t('pause.statusResumed'));
    this.dispatchEvent(
      new CustomEvent<PauseChangeDetail>('pauseChanged', { detail: { paused: this.paused } })
    );
    this.eventHub?.emit(PAUSE_CHANGE_EVENT, { paused: this.paused });
  }

  public handleInput(event: KeyboardEvent): boolean {
    if (event.key !== 'Escape') {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.setPaused(!this.paused);
    return true;
  }

  private handleToggleClick = (): void => {
    this.setPaused(false);
  };

  private setStatus(text: string): void {
    if (this.statusElement) {
      this.statusElement.textContent = text;
    }
  }
}
